import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import useSWR from "swr";
import urls from "../../api/urls"
import {api, HydraData} from "../../api/apiProvider";
import {Card, Flex, Form, Input, message, Select} from "antd";
import ButtonBlack from "../../components/ui/buttons/ButtonBlack";
import {useParams} from "react-router-dom";
import MediaFactors from "../../components/brief/managerTasks/media/MediaFactors";
import {BriefManager, Channel, Kpi, ModelType, Product} from "../../api/types";
import type {DefaultOptionType} from "antd/es/select";
import BriefTaskFactors from "../../components/brief/managerTasks/BriefTaskFactors";
import IconClose from "../../components/ui/icons/IconClose";
import Information from "../../components/information";
import {useLayoutContext} from "./EditLayout";
import KpiData from "../../components/brief/managerTasks/KpiData";
import dayjs from "dayjs";
import getBriefParamsString from "../../api/params";
import {isEqualValues} from "../../utils";
import Title from "antd/es/typography/Title";
import {CSSTransition} from "react-transition-group";
import Stages from "../../components/brief/Stages";
import {debounce} from "lodash";
import Roles from "../../roles/Roles";

const dateFormat = 'DD.MM.YYYY'

interface Model {
  id: string,
  '@id': string,
  name: string,
}

interface BriefChannel {
  '@id': string,
  channel: Channel,
  id: number,
}

interface BriefDataType {
  '@context': string,
  '@id': string,
  '@type': string,
  products: Product[],
  commentary: string,
  dataDateEnd: string,
  dataDateStart: string,
  dateEnd: string,
  id: string;
  manager?: string,
  name: string,
  product: Model,
  seasonalityText: string,
  tasks: string[],
  isAccepted: boolean,
  modelType: ModelType,
  kpi: Kpi,
}

interface FormData {
  value: string,
  label: string,
  briefChannel: string,
}

const addBriefChannel = async (data: any) => {
  return await api.post(`${urls.briefChannels}`, {arg: data})
}

const deleteBriefChannel = async (briefChannelId: number) => {
  return await api.delete(`${urls.briefChannels}/${briefChannelId}`)
}

export default function ManagerTasks() {
  const {t} = useTranslation()
  const [managersOptions, setManagersOptions] = useState<{ value: string, label: string }[]>([])

  const [briefData, setBriefData] = useState<BriefDataType>()
  const [channelSearch, setChannelSearch] = useState<string>('')
  const [channelsOptions, setChannelsOptions] = useState<DefaultOptionType[]>([])
  const [selectedBriefChannels, setSelectedBriefChannels] = useState<DefaultOptionType[]>([])
  const [selectedCommentaryId, setSelectedCommentaryId] = useState(0)
  const {setBrief, brief: layoutBrief} = useLayoutContext()
  const [initialValues, setInitialValues] = useState<FormData[]>([])
  const [isValuesChanged, setIsValuesChanged] = useState<boolean>(false)
  const stagesNodeRef = useRef(null)
  const stagesButtonNodeRef = useRef(null)
  const [showStages, setShowStages] = useState(false)
  const [showStagesButton, setShowStagesButton] = useState(true)

  const routeParams = useParams()
  const briefId = Number(routeParams.id) || 0
  const [briefForm] = Form.useForm()

  const {
    data: brief,
    mutate: mutateBrief,
    isValidating: isBriefValidating,
  } = useSWR<{
    data: BriefDataType
  }>(briefId > 0 && briefId !== 0 ? `${urls.briefs}/${briefId}?${getBriefParamsString()}` : null, api.get, {
    revalidateOnFocus: false
  })

  const {
    data: managers,
    isValidating: isManagersValidating,
  } = useSWR<HydraData>(`${urls.briefs}/${briefId}/managers?t[]=manager`, api.get, {
    revalidateOnFocus: false
  })

  const {
    data: briefChannels,
    mutate: mutateBriefChannels,
  } = useSWR<{
    data: BriefChannel[]
  }>(briefId > 0 && briefId !== 0 ? `${urls.briefChannels}?brief.id=${briefId}&t[]=channelTask&t[]=briefChannel&t[]=dict&t[]=manager&t[]=dict:product` : null, api.get, {
    revalidateOnFocus: false
  })

  const getChannelsParamsString = () => {
    const params = new URLSearchParams()

    if (channelSearch) {
      params.append('name', channelSearch)
    }
    params.append('pagination', 'false')

    return params.toString()
  }

  const {
    data: channels,
    mutate: mutateChannels,
  } = useSWR<HydraData>(`${urls.channels}?${getChannelsParamsString()}`, api.get, {
    revalidateOnFocus: false
  })

  useEffect(() => {
    if (channels && channels.data) {
      setChannelsOptions(channels.data.map((channel: Model) => {
        return {
          value: channel['@id'],
          label: channel.name,
        }
      }))
    }
  }, [channels])

  useEffect(() => {
    if (briefChannels && briefChannels.data) {
      const selectedBriefChannels = briefChannels.data.map((briefChannel: BriefChannel) => {
        return {
          value: briefChannel.channel['@id'],
          label: briefChannel.channel.name,
          briefChannel: briefChannel['@id'],
        }
      })
      setSelectedBriefChannels(selectedBriefChannels)
      setInitialValues(selectedBriefChannels)
      briefForm.setFieldValue('channels', selectedBriefChannels)
    }
  }, [briefChannels])

  useEffect(() => {
    if (brief && brief.data) {
      const briefData: BriefDataType = brief.data
      setBriefData(briefData)
      setBrief({
        isAccepted: brief.data.isAccepted,
        isPao: brief.data.products && brief.data.products.some((product: Product) => product.company &&
          product.company.isBriefManyChannels)
      })

      if (brief.data.products && brief.data.products.length > 0) {
        const product = brief.data.products[0]
        const company = product.company

        if (company) {
          briefForm.setFieldValue('company', company.name)

          const pool = company.pool

          if (pool) {
            briefForm.setFieldValue('pool', pool.name)
          }
        }

        briefForm.setFieldValue('product', product.name)
      }

      briefForm.setFieldValue(
        'period',
        `${dayjs(brief.data.dataDateStart).format(dateFormat)} - ${dayjs(brief.data.dataDateEnd).format(dateFormat)}`
      )
    }
  }, [brief])

  useEffect(() => {
    if (managers && managers.data) {
      setManagersOptions(managers.data.map((manager: BriefManager) => ({
        label: manager.manager.email,
        value: manager.manager['@id'],
      })))
    }
  }, [managers])

  const isBriefManyChannels = () => {
    if (!brief || !brief.data || !brief.data.products) {
      return false
    }

    return brief.data.products.every((product: Product) => product.company && product.company.isBriefManyChannels)
  }

  const onChannelsSave = (values: any) => {
    if (!Roles.hasAccess('canReadAndUpdateBriefChannels')) {
      return
    }

    const briefChannelsToDelete = []
    for (let selectedBriefChannel of selectedBriefChannels) {
      const channelInValues = values.channels.find((briefChannel: DefaultOptionType) => briefChannel.value === selectedBriefChannel.value)
      if (!channelInValues && selectedBriefChannel.value && briefChannels) {
        const briefChannel = briefChannels.data.find((briefChannel: BriefChannel) => briefChannel.channel['@id'] === selectedBriefChannel.value)
        if (briefChannel) {
          briefChannelsToDelete.push(briefChannel.id)
        }
      }
    }

    Promise.all(
      [...values.channels.filter((channel: DefaultOptionType) => {
        return !selectedBriefChannels.find((briefChannel: DefaultOptionType) => briefChannel.value === channel.value)
      })
      .map((channel: DefaultOptionType) => {
          return addBriefChannel({
            brief: `/api${urls.briefs}/${briefId}`,
            channel: channel.value,
          })
        }
      ),
        ...briefChannelsToDelete.map((id: number) => {
          return deleteBriefChannel(id)
        })]
    ).then((responses) => {
      message.success(t('common.saved'));
      mutateBrief()
      mutateBriefChannels()
      setIsValuesChanged(false)
    }).catch(errors => {
      if (errors['hydra:description']) {
        message.error(errors['hydra:description'])
      }
      setIsValuesChanged(false)
    })
  }

  const onValuesChange = (allValues: any) => {
    if (allValues.length !== initialValues?.length) {
      setIsValuesChanged(true)
      return
    }

    for (let key in initialValues) {
      if (!initialValues.hasOwnProperty(key)) {
        continue
      }

      const initialValue = initialValues[Number(key)]
      if (key in allValues) {
        const changedValue = allValues[key]

        if (!isEqualValues(initialValue, changedValue)) {
          setIsValuesChanged(true)
          return
        }
      }
    }

    setIsValuesChanged(false)
  }

  return (
    <div className={'page-wrapper manager-tasks'}>
      <section className={'main-section'}>
        <Flex gap={'large'} vertical>
          <Form
            form={briefForm}
            layout="vertical"
            name="brief"
            onFinish={onChannelsSave}
            onValuesChange={onValuesChange}
          >
            <Card>
              <Flex gap={'middle'} align={'flex-end'} wrap={'wrap'}>
                <Flex gap={'middle'} wrap={'wrap'}>
                  <Form.Item name="pool" label={t('brief.pool')}>
                    <Input
                      disabled
                      style={{color: '#626E75'}}
                    />
                  </Form.Item>
                  <Form.Item name="company" label={t('brief.company')}>
                    <Input
                      disabled
                      style={{color: '#626E75'}}
                    />
                  </Form.Item>
                  <Form.Item name="product" label={t('brief.product')}>
                    <Input
                      disabled
                      style={{color: '#626E75'}}
                    />
                  </Form.Item>
                  {Roles.hasAccess('canReadAndUpdateBriefChannels') &&
                    <Form.Item name="channels" label={t('brief.channel')} rules={[{required: true}]}>
                    <Select
                    className={'manager-tasks__channels'}
                    allowClear
                    labelInValue
                    mode="multiple"
                    maxCount={isBriefManyChannels() ? 1000 : 1}
                    onSearch={debounce((value: string) => setChannelSearch(value), 600)}
                    placeholder={t('common.choose')}
                    optionFilterProp="children"
                    filterOption={false}
                    options={channelsOptions}
                    />
                    </Form.Item>
                  }
                  <Form.Item name="period" label={t('brief.period')}>
                    <Input
                      disabled
                      style={{color: '#626E75', minWidth: '211px'}}
                    />
                  </Form.Item>
                  {Roles.hasAccess('canReadAndUpdateBriefChannels') &&
                    <Form.Item label={' '}>
                      <ButtonBlack
                        onClick={briefForm.submit}
                        disabled={!isValuesChanged}
                      >{t('common.save')}</ButtonBlack>
                    </Form.Item>
                  }
                </Flex>
              </Flex>
            </Card>
          </Form>
          <Flex vertical gap={32}>
            {briefData !== undefined &&
              <>
                <KpiData
                  brief={briefData}
                  selectedCommentaryId={selectedCommentaryId}
                  setSelectedCommentaryId={setSelectedCommentaryId}
                />
                <Flex vertical gap={10}>
                  <Title level={5}>{t('managerTasks.mediaFactors')}</Title>
                  <MediaFactors
                    brief={briefData}
                    selectedCommentaryId={selectedCommentaryId}
                    setSelectedCommentaryId={setSelectedCommentaryId}
                    type={'online'}
                  />
                  <MediaFactors
                    brief={briefData}
                    selectedCommentaryId={selectedCommentaryId}
                    setSelectedCommentaryId={setSelectedCommentaryId}
                    type={'offline'}
                  />
                </Flex>
                <BriefTaskFactors
                  brief={briefData}
                  taskType={'product'}
                  title={t('brief.productFactors')}
                  selectedCommentaryId={selectedCommentaryId}
                  setSelectedCommentaryId={setSelectedCommentaryId}
                />
                <BriefTaskFactors
                  brief={briefData}
                  taskType={'external'}
                  title={t('brief.externalFactors')}
                  selectedCommentaryId={selectedCommentaryId}
                  setSelectedCommentaryId={setSelectedCommentaryId}
                />
              </>
            }
          </Flex>
        </Flex>
      </section>
      {selectedCommentaryId > 0 &&
        <section className={'side-section'}>
          <div className={'side-section__close'} onClick={() => setSelectedCommentaryId(0)}>
            <IconClose/>
          </div>
          <Information briefId={0} updateList={() => {
          }} commentaryId={selectedCommentaryId} showStages={false}/>
        </section>
      }

      <CSSTransition
        in={showStagesButton}
        nodeRef={stagesButtonNodeRef}
        timeout={400}
        classNames="criterion-stages-button"
        unmountOnExit
      >
        <div className={'criterion__stages-button'} onClick={() => {
          setShowStages(true)
          setShowStagesButton(false)
        }} ref={stagesButtonNodeRef}>
          <Flex justify={'space-between'} align={'center'}>
            <span className={'criterion__stages-button__text'}>{t('edit.stages')}</span>
            <span className={'criterion__stages-button__expand'}>{t('tasks.expand')}</span>
          </Flex>
        </div>

      </CSSTransition>

      <CSSTransition
        in={showStages}
        nodeRef={stagesNodeRef}
        timeout={400}
        classNames="criterion-stages"
        unmountOnExit
        onExiting={() => setShowStagesButton(true)}
      >
        <div className={'criterion__stages-block'} ref={stagesNodeRef}>
          <Flex justify={'space-between'} align={'center'} onClick={() => setShowStages(false)}>
            <span className={'criterion__stages-button__text'}>{t('edit.stages')}</span>
            <span className={'criterion__stages-button__expand'}>{t('tasks.collapse')}</span>
          </Flex>
          <Stages />
        </div>
      </CSSTransition>
    </div>
  )
}
